import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  MenuItem,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  makeStyles
} from "@material-ui/core";
import { Pagination } from "@material-ui/lab";
import { Error as ErrorIcon } from "@material-ui/icons";
import { toast } from "react-toastify";
import { getVPSActionHistory } from "../../../../services/vps.api";
import { useTimezone } from "../../../../hooks/useTimezone";

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(2)
  },
  header: {
    marginBottom: theme.spacing(2)
  },
  filtersContainer: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.paper
  },
  table: {
    marginTop: theme.spacing(2)
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 300
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(2)
  },
  emptyContainer: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary
  }
}));

const VPSActionHistory = () => {
  const classes = useStyles();
  const { formatDateTime } = useTimezone();
  const [history, setHistory] = useState([]);
  const [filters, setFilters] = useState({
    actionType: "",
    result: "",
    startDate: "",
    endDate: ""
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      // Convertir filtros vacíos a null para el backend
      const params = {
        page: pagination.page,
        limit: pagination.limit
      };
      
      // Solo agregar filtros si tienen valor
      if (filters.actionType && filters.actionType.trim() !== "") {
        params.actionType = filters.actionType;
      }
      if (filters.result && filters.result.trim() !== "") {
        params.result = filters.result;
      }
      if (filters.startDate && filters.startDate.trim() !== "") {
        params.startDate = filters.startDate;
      }
      if (filters.endDate && filters.endDate.trim() !== "") {
        params.endDate = filters.endDate;
      }

      const { data } = await getVPSActionHistory(params);

      if (data.success) {
        const historyData = data.data.history || [];
        console.log("[VPSActionHistory] Datos recibidos:", {
          total: data.data.pagination?.total,
          registros: historyData.length,
          pagina: data.data.pagination?.page
        });
        setHistory(historyData);
        setPagination((prev) => ({
          ...prev,
          total: data.data.pagination?.total || 0,
          totalPages: data.data.pagination?.totalPages || 0
        }));
        setInitialLoad(false); // Marcar que la carga inicial se completó
      } else {
        throw new Error(data.error || "Error obteniendo historial");
      }
    } catch (err) {
      const errorMessage =
        err.response?.data?.error || err.message || "Error desconocido";
      toast.error(errorMessage);
      console.error("Error obteniendo historial de acciones:", err);
      // Limpiar datos en caso de error
      setHistory([]);
      setPagination((prev) => ({
        ...prev,
        total: 0,
        totalPages: 0
      }));
      setInitialLoad(false); // Marcar que la carga inicial se completó (aunque haya error)
    } finally {
      setLoading(false);
    }
  };

  // Cargar datos cuando se monta el componente o cambia la página
  useEffect(() => {
    fetchHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value
    }));
    // Resetear a página 1 cuando cambian los filtros
    setPagination((prev) => ({
      ...prev,
      page: 1
    }));
  };

  const handleApplyFilters = () => {
    setPagination((prev) => ({
      ...prev,
      page: 1
    }));
    fetchHistory();
  };

  const getActionTypeLabel = (actionType) => {
    const labels = {
      cleanup: "Limpieza",
      optimize: "Optimización",
      emergency: "Emergencia"
    };
    return labels[actionType] || actionType;
  };

  const getResultColor = (result) => {
    const colors = {
      success: "primary",
      error: "secondary",
      warning: "default"
    };
    return colors[result] || "default";
  };

  return (
    <Box className={classes.container}>
      <Typography variant="h6" className={classes.header}>
        Historial de Acciones VPS
      </Typography>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        Registro de acciones manuales y automáticas ejecutadas en el sistema
      </Typography>

      {/* Filtros básicos */}
      <Paper className={classes.filtersContainer}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              label="Tipo de Acción"
              value={filters.actionType}
              onChange={(e) => handleFilterChange("actionType", e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              <MenuItem value="cleanup">Limpieza</MenuItem>
              <MenuItem value="optimize">Optimización</MenuItem>
              <MenuItem value="emergency">Emergencia</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              select
              fullWidth
              label="Resultado"
              value={filters.result}
              onChange={(e) => handleFilterChange("result", e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="success">Éxito</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="warning">Advertencia</MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="date"
              label="Fecha Inicio"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth
              type="date"
              label="Fecha Fin"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
        </Grid>
        <Box mt={2}>
          <button
            onClick={handleApplyFilters}
            style={{
              padding: "8px 16px",
              backgroundColor: "#1976d2",
              color: "white",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer"
            }}
          >
            Aplicar Filtros
          </button>
        </Box>
      </Paper>

      {/* Tabla de historial simplificada */}
      <Paper className={classes.table}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Fecha/Hora</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Ejecución</TableCell>
              <TableCell>Ejecutado Por</TableCell>
              <TableCell>Resultado</TableCell>
              <TableCell>Espacio Liberado</TableCell>
              <TableCell>Error</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box className={classes.loadingContainer}>
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            ) : history.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box className={classes.emptyContainer}>
                    No hay registros
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              history.map((action) => (
                <TableRow key={action.id}>
                  <TableCell>
                    {action.createdAt ? formatDateTime(action.createdAt) : "-"}
                  </TableCell>
                  <TableCell>
                    {getActionTypeLabel(action.actionType)}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={
                        action.executionType === "automática"
                          ? "Automática"
                          : "Manual"
                      }
                      color={
                        action.executionType === "automática"
                          ? "default"
                          : "primary"
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{action.executedBy || "N/A"}</TableCell>
                  <TableCell>
                    <Chip
                      label={action.result}
                      color={getResultColor(action.result)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {action.spaceFreedMB
                      ? `${action.spaceFreedMB} MB`
                      : "-"}
                  </TableCell>
                  <TableCell>
                    {action.errorMessage ? (
                      <Tooltip title={action.errorMessage}>
                        <IconButton size="small">
                          <ErrorIcon color="error" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      "-"
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Paginación - Solo mostrar si hay registros Y se completó la carga inicial */}
      {!loading && 
       !initialLoad && 
       history.length > 0 && 
       pagination.total > 0 && 
       pagination.totalPages > 0 && (
        <Box className={classes.paginationContainer}>
          <Pagination
            count={pagination.totalPages}
            page={pagination.page}
            onChange={(e, page) =>
              setPagination((prev) => ({ ...prev, page }))
            }
            color="primary"
          />
        </Box>
      )}

      {/* Total de registros - Solo mostrar si se completó la carga inicial */}
      {!loading && !initialLoad && (
        <Typography
          variant="caption"
          color="textSecondary"
          style={{ marginTop: 8, display: "block", textAlign: "center" }}
        >
          {history.length > 0 && pagination.total > 0
            ? `Total: ${pagination.total} registros`
            : "No hay registros para mostrar"}
        </Typography>
      )}
    </Box>
  );
};

export default VPSActionHistory;
